import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, LOGOS } from "../lib/api";
import { ShieldCheck, Lock, Mail, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@edf.org");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If already logged in, redirect
    const token = localStorage.getItem("edf_token");
    if (token) {
      api.get("/auth/me").then(() => navigate("/admin")).catch(() => localStorage.removeItem("edf_token"));
    }
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("edf_token", data.access_token);
      localStorage.setItem("edf_admin", JSON.stringify(data.user));
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] grain relative flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#E07A5F]/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#1B4332]/10 blur-3xl" />
      </div>

      <button onClick={() => navigate("/")} className="absolute top-6 left-6 inline-flex items-center gap-2 text-sm text-[#5C6661] hover:text-[#1B4332]" data-testid="back-to-home">
        <ArrowLeft className="w-4 h-4" /> Back to website
      </button>

      <div className="relative w-full max-w-md">
        <div className="bg-white rounded-2xl border border-border p-8 sm:p-10 shadow-xl">
          <div className="flex justify-center mb-6">
            <img src={LOGOS.main} alt="EDSW" className="w-20 h-20 object-contain" />
          </div>
          <div className="text-center">
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#E07A5F] font-semibold">
              <ShieldCheck className="w-3.5 h-3.5" /> Admin Access
            </div>
            <h1 className="font-serif-display text-3xl text-[#1B4332] mt-2">Welcome Back</h1>
            <p className="text-sm text-[#5C6661] mt-1">Sign in to manage your foundation</p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#5C6661] font-semibold">Email</label>
              <div className="mt-1.5 relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6661]" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-border rounded-xl text-sm focus:outline-none focus:border-[#1B4332] focus:ring-1 focus:ring-[#1B4332]"
                  data-testid="admin-email-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#5C6661] font-semibold">Password</label>
              <div className="mt-1.5 relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6661]" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-border rounded-xl text-sm focus:outline-none focus:border-[#1B4332] focus:ring-1 focus:ring-[#1B4332]"
                  data-testid="admin-password-input"
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm" data-testid="admin-login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1B4332] text-white py-3 rounded-xl text-sm font-semibold hover:bg-[#2D6A4F] transition-colors disabled:opacity-50"
              data-testid="admin-login-button"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

        </div>
      </div>
    </div>
  );
}
